import { setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getGeoCodeFromAddress, getGeoCodeFromLatLng } from "@insite/client-framework/Common/Utilities/GoogleMaps/getGeoCodeFromAddress";
import translate from "@insite/client-framework/Translate";
import { WarehouseDto } from "@insite/client-framework/Types/ApiModels";
import Search from "@insite/mobius/Icons/Search";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import FieldSetPresentationProps from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductPreview from "@store/Components/Store/Product/ProductPreview";
import StoreWarehouseAvailabilityInfo from "@store/Components/Store/StoreWarehouseAvailabilityInfo";
import { COOKIES } from "@store/constants";
import { DistanceUnitOfMeasure } from "@store/Overrides/Components/DistanceDisplay";
import { TYPOGRAPHY } from "@store/theme/design";
import getUomCookie from "@store/utilities/getUomCookie";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    warehouses?: WarehouseDto[];
    currentWarehouseName?: string;
    isHighDemandMode?: boolean;
    updateWarehouses: (latitude: string, longitude: string) => void;
    isWarehousesUpdating: boolean;
}

type Props = OwnProps;

export interface ProductAvailabilityStyles {
    wrapper?: InjectableCss;
    productPreview: InjectableCss;
    warehouseList: InjectableCss;
    warehouseSearch: InjectableCss;
    warehouseRadio: InjectableCss;
    searchTitle: TypographyPresentationProps;
    warehouseAvailability?: TypographyPresentationProps;
    searchTextField?: TextFieldPresentationProps;
    distanceUnitOfMeasureRadioGroup?: RadioGroupProps;
    distanceUnitOfMeasureRadio?: FieldSetPresentationProps<RadioComponentProps>;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    productPreview: {
        css: css`
            padding: 0 1.5em 1.5em;
            margin-bottom: 1em;
            background-color: ${getColor("common.background")};
        `
    },
    warehouseList: {
        css: css`
            padding: 1.5em;
            background-color: ${getColor("common.background")};
        `
    },
    warehouseSearch: {
        css: css`
            margin-bottom: .5em;
        `
    },
    warehouseRadio: {
        css: css`
            display: flex;
            align-items: center;
            margin-bottom: .5em;
        `
    },
    searchTitle: {
        weight: TYPOGRAPHY.weight.bold,
        size: TYPOGRAPHY.size.large,
        css: css`
            display: block;
            margin-bottom: 0.5em;
        `
    },
    distanceUnitOfMeasureRadioGroup: {
        css: css`
            display: inline-block;
            flex-direction: row;
            margin-left: auto;
            text-align: right;
            & > div {
                display: inline-flex;
            }
        `,
    },
    distanceUnitOfMeasureRadio: {
        css: css`
            margin-top: 0 !important;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 80vh;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
};

const styles = productAvailabilityStyles;

const StoreWarehouseAvailability: FC<Props> = ({
    warehouses,
    currentWarehouseName,
    isHighDemandMode,
    updateWarehouses,
    isWarehousesUpdating,
}) => {
    const uomCookie = getUomCookie();
    const [searchAvailabilityFilter, setAvailabilityLocationFilter] = useState("");
    const [distanceUnitOfMeasure, setDistanceUnitOfMeasure] = useState<DistanceUnitOfMeasure>(uomCookie);
    const defaultDistance = "0";

    const doSearch = () => {
        if (searchAvailabilityFilter) {
            getGeoCodeFromAddress(searchAvailabilityFilter)
                .then(result => {
                    const geocoderResult = result[0].geometry.location;

                    getGeoCodeFromLatLng(geocoderResult.lat(), geocoderResult.lng())
                        .then(result => {
                            setAvailabilityLocationFilter(result[0].formatted_address);
                        })

                    updateWarehouses(geocoderResult.lat().toString(), geocoderResult.lng().toString());
                })
        } else {
            updateWarehouses(defaultDistance, defaultDistance);
        }
    };

    const handleDistanceUnitOfMeasureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as DistanceUnitOfMeasure;
        setCookie(COOKIES.branchDistanceUom, value.toString());
        setDistanceUnitOfMeasure(value);
    };

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="availabilityByWarehouseModal">
            <StyledWrapper {...styles.productPreview}>
                <ProductPreview />
            </StyledWrapper>
            <StyledWrapper {...styles.warehouseList}>
                <StyledWrapper {...styles.warehouseSearch}>
                    <Typography {...styles.searchTitle}>{translate("Select Pickup Location")}</Typography>
                    <SearchAvailabilityTextField
                        styles={styles.searchTextField}
                        doSearch={doSearch}
                        availabilitySearchFilter={searchAvailabilityFilter}
                        setAvailabilitySearchFilter={setAvailabilityLocationFilter}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.warehouseRadio}>
                    <Typography>{translate("{0} Results").replace("{0}", `${warehouses?.length}`)}</Typography>
                    <RadioGroup
                        {...styles.distanceUnitOfMeasureRadioGroup}
                        value={distanceUnitOfMeasure}
                        onChangeHandler={handleDistanceUnitOfMeasureChange}
                    >
                        <Radio {...styles.distanceUnitOfMeasureRadio} value="Imperial">
                            {translate("Miles")}
                        </Radio>
                        <Radio {...styles.distanceUnitOfMeasureRadio} value="Metric">
                            {translate("Kilometers")}
                        </Radio>
                    </RadioGroup>
                </StyledWrapper>
                {isWarehousesUpdating ? (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                ) : (
                    warehouses?.map(warehouse =>
                        <StyledWrapper key={warehouse.name}>
                            <StoreWarehouseAvailabilityInfo
                                warehouse={warehouse}
                                currentWarehouseName={currentWarehouseName}
                                isHighDemandMode={isHighDemandMode}
                                distanceUnitOfMeasure={distanceUnitOfMeasure}
                            />
                        </StyledWrapper>
                    )
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

interface SearchAvailabilityTextFieldProps {
    styles?: TextFieldPresentationProps;
    availabilitySearchFilter: string;
    setAvailabilitySearchFilter: (filter: string) => void;
    doSearch: (filter: string) => void;
}

const SearchAvailabilityTextField = ({
    styles,
    availabilitySearchFilter,
    setAvailabilitySearchFilter,
    doSearch,
}: SearchAvailabilityTextFieldProps) => {

    const handleSearchLocationsChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAvailabilitySearchFilter(event.target.value); // Poolcorp Custom Code
    };

    const iconOnClick = (event: React.MouseEvent) => {
        event.preventDefault();
        doSearch(availabilitySearchFilter);
    };

    const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            doSearch(availabilitySearchFilter);
        }
    };

    return (
        <TextField
            {...styles}
            value={availabilitySearchFilter}
            onChange={handleSearchLocationsChanged}
            placeholder={translate("Search by location name, city, state, or zip")}
            data-test-selector="productAvailabilityModal_locationSearch"
            onKeyPress={onKeyPress}
            iconProps={{ src: Search }}
            iconClickableProps={{ onClick: iconOnClick }}
        />
    );
};

export default StoreWarehouseAvailability;
